import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getAttributesFromSettings } from '@/lib/settings-api';

interface PlatformSetting {
  id: string;
  key: string;
  value: any;
  description: string | null;
  updated_at: string;
}

export interface PlatformAnnouncement {
  active: boolean;
  type: 'info' | 'warning' | 'maintenance';
  title: string;
  message: string;
  startDate: string | null;
  endDate: string | null;
  blockAccess: boolean;
  version?: number;
}

const defaultAnnouncement: PlatformAnnouncement = {
  active: false,
  type: 'info',
  title: '',
  message: '',
  startDate: null,
  endDate: null,
  blockAccess: false,
};

export function usePlatformSettings() {
  const queryClient = useQueryClient();

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      // Read via public edge function so anonymous (pre-login) pages keep working
      // after removing anon RLS on platform_settings.
      const { data, error } = await supabase.functions.invoke('platform-settings', { method: 'GET' });
      if (error) throw error;
      const obj = (data ?? {}) as Record<string, any>;
      const rows: PlatformSetting[] = Object.entries(obj)
        .filter(([k]) => k !== '__feature_flags')
        .map(([key, value]) => ({
          id: key,
          key,
          value,
          description: null,
          updated_at: new Date().toISOString(),
        }));
      return rows;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const getSetting = (key: string, defaultValue: any = null): any => {
    const setting = settings.find(s => s.key === key);
    return setting?.value ?? defaultValue;
  };

  const videosEnabled = getSetting('videos_enabled', false) === true;
  const landingBlogVisible = getSetting('landing_blog_visible', true) !== false;
  // Fallback a versión anterior por si alguien lo usa, pero usaremos el anuncio.
  const maintenanceMode = getSetting('maintenance_mode', false) === true;
  const showFlashcardExplanations = getSetting('show_flashcard_explanations', true);
  const showQuestionExplanations = getSetting('show_question_explanations', true);
  const showExplanationImages = getSetting('show_explanation_images', true);

  
  const announcement: PlatformAnnouncement = getSetting('platform_announcement', defaultAnnouncement);
  
  // Calcular si estamos actualmente en mantenimiento estricto basado en las fechas
  let isMaintenanceActive = false;
  
  if (announcement?.active && announcement?.blockAccess) {
    const now = new Date();
    const start = announcement.startDate ? new Date(announcement.startDate) : null;
    const end = announcement.endDate ? new Date(announcement.endDate) : null;
    
    if (start && end) {
      isMaintenanceActive = now >= start && now <= end;
    } else if (start && !end) {
      isMaintenanceActive = now >= start;
    } else if (!start && end) {
      isMaintenanceActive = now <= end;
    } else {
      isMaintenanceActive = true; // Si bloquea acceso pero no hay fechas, bloquea por defecto
    }
  }
  
  const contactInfo = {
    email: getSetting('contact_email', 'contacto@repasogpc.com'),
    phone: getSetting('contact_phone', ''),
    whatsapp: getSetting('contact_whatsapp', ''),
  };

  const socialLinks = {
    instagram: getSetting('social_instagram', ''),
    twitter: getSetting('social_twitter', ''),
    facebook: getSetting('social_facebook', ''),
    youtube: getSetting('social_youtube', ''),
    linkedin: getSetting('social_linkedin', ''),
    tiktok: getSetting('social_tiktok', ''),
  };

  const seoSettings = {
    title: getSetting('seo_title', 'RepasoGPC - Preparación ENARM'),
    description: getSetting('seo_description', ''),
    keywords: getSetting('seo_keywords', ''),
  };

  const googleAnalyticsId = getSetting('google_analytics_id', '');
  const facebookPixelId = getSetting('facebook_pixel_id', '');

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('platform_settings' as any)
        .upsert({
          key,
          value,
          updated_by: user?.id,
          updated_at: new Date().toISOString()
        } as any, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
    },
  });

  return {
    settings,
    isLoading,
    videosEnabled,
    landingBlogVisible,
    maintenanceMode,
    announcement,
    isMaintenanceActive,
    contactInfo,
    socialLinks,
    seoSettings,
    googleAnalyticsId,
    facebookPixelId,
    showFlashcardExplanations,
    showQuestionExplanations,
    showExplanationImages,
    registrationsEnabled: getSetting('registrations_enabled', true) !== false,
    getSetting,
    updateSetting,
  };
};

// Hook para obtener atributos de usuario (universidades, estados, instituciones, especialidades)
export function useUserAttributes() {
  return useQuery({
    queryKey: ['user-attributes'],
    queryFn: getAttributesFromSettings,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

// Helpers para obtener atributos específicos desde el hook
export function useUserAttribute(attribute: 'universities' | 'states' | 'institutions' | 'specialties') {
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useUserAttributes();
  
  const value = data?.[attribute] ?? [];
  
  const updateAttribute = useMutation({
    mutationFn: async (newValues: string[]) => {
      const { saveAttributesToSettings } = await import('@/lib/settings-api');
      await saveAttributesToSettings(attribute, newValues);
    },
    onSuccess: () => {
      // Invalidate the parent query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['user-attributes'] });
    },
    onMutate: async (newValues: string[]) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ['user-attributes'] });
      const previous = queryClient.getQueryData(['user-attributes']);
      queryClient.setQueryData(['user-attributes'], (old: any) => {
        if (!old) return { [attribute]: newValues };
        return { ...old, [attribute]: newValues };
      });
      return { previous };
    },
    onError: (err, newValues, context: any) => {
      queryClient.setQueryData(['user-attributes'], context?.previous);
    },
  });

  return {
    value,
    isLoading,
    updateAttribute: updateAttribute.mutateAsync,
  };
}
